import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import { colors } from "../../lib/output.js";
import { getFoundryToken } from "../../lib/auth.js";
import { findOnboardingProfile } from "../../lib/onboarding-profile.js";
import { resolveSeedContext, applyProfileToBrain } from "../../lib/company-profile-seed.js";

export class BootstrapSeedProfileCommand extends M8tCommand {
  static paths = [["bootstrap", "seed-profile"]];
  static usage = Command.Usage({
    description: "Seed Clever Stacey's brain with the company profile from the onboarding questionnaire.",
    details:
      "Reads the latest stacey-intake conversation, renders memory/company-profile.md (+ its MEMORY.md index line), and commits both to <org>/stacey-brain via the GitHub App. Idempotent. --watch polls until the founder completes the questionnaire.",
    examples: [
      ["Seed now (idempotent)", "$0 bootstrap seed-profile"],
      ["Wait for the founder to finish", "$0 bootstrap seed-profile --watch"],
    ],
  });

  endpoint = Option.String("--endpoint", { description: "Override the Foundry endpoint (else read from the install status)." });
  brain = Option.String("--brain", { description: "Override the brain repo (default <org>/stacey-brain)." });
  watch = Option.Boolean("--watch", false, { description: "Poll until the questionnaire completes (or --timeout)." });
  timeout = Option.String("--timeout", { description: "Watch timeout in minutes (default 20)." });
  githubAppCreds = Option.String("--github-app-creds");

  async executeCommand(): Promise<number> {
    const ctx = await resolveSeedContext({
      endpointOverride: typeof this.endpoint === "string" ? this.endpoint : undefined,
      brainOverride: typeof this.brain === "string" ? this.brain : undefined,
      credsPath: typeof this.githubAppCreds === "string" ? this.githubAppCreds : undefined,
    });
    if (!ctx) {
      this.context.stderr.write(`  ${colors.dim("no GitHub App creds (~/.m8t-stack/github-app.json) — no brain to seed; skipping.")}\n`);
      return 0;
    }
    const watch = this.watch === true;
    const rawTimeout = typeof this.timeout === "string" ? this.timeout.trim() : "";
    const parsedTimeout = Number(rawTimeout);
    // Honor an explicit finite value (including 0); fall back to 20 for unset/empty/non-numeric.
    const timeoutMin = rawTimeout !== "" && Number.isFinite(parsedTimeout) ? parsedTimeout : 20;
    const deadline = Date.now() + timeoutMin * 60_000;
    const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

    for (;;) {
      const token = await getFoundryToken();
      const { hadIntake, block } = await findOnboardingProfile({ endpoint: ctx.endpoint, token });
      if (block) {
        await applyProfileToBrain({ block, brainRepo: ctx.brainRepo, branch: "main", appCreds: ctx.appCreds });
        this.context.stdout.write(`${colors.success("✓")} seeded Clever Stacey's brain (${ctx.brainRepo}) from your questionnaire.\n`);
        return 0;
      }
      if (!watch || Date.now() >= deadline) {
        this.context.stderr.write(
          `  ${colors.dim(hadIntake ? "questionnaire not complete yet — no m8t_onboarding block found." : "no onboarding questionnaire found.")}\n` +
          `  ${colors.hint("retry:")} m8t bootstrap seed-profile\n`,
        );
        return 3;
      }
      this.context.stderr.write(`  ${colors.dim("waiting for the questionnaire to complete…")}\n`);
      await sleep(20_000);
    }
  }
}
